import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "jquery-ui/dist/themes/ui-lightness/jquery-ui.css";
import { formatShortPrice, formatNumberIndian } from "../../utils/formatNumber";
import { handleApiError } from "../../utils/apiHelpers";
import FrontendLayout from "../../components/frontend/FrontendLayout";
import NoResults from "../../components/admin/common/NoResults";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import ContactPopup from "../../components/frontend/ContactPopup";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface Land {
  ln_id: number;
  ln_locality: string;
  ln_address: string;
  ln_total_area_sqft: number;
  ln_total_sqft_area: string;
  ln_seo_url: number;
  ln_plottype: string;
  ln_post_date: string;
  ln_price: number;
  ln_image?: string;
  image_files?: string[];
  ln_status: number;
  ln_admin_approve: number;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface DropdownOptions {
  classifications: { cls_id: number; cls_name: string; cls_status: number }[];
  zones: { z_id: number; z_name: string; "Z-status": number }[];
  plotTypes: { pt_id: number; pt_name: string; pt_status: number }[];
  facing: { fc_id: number; fc_name: string; fc_status: number }[];
}

const ITEMS_PER_PAGE = 6;

const LandList: React.FC = () => {
  const [lands, setLands] = useState<Land[]>([]);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [wishlistMap, setWishlistMap] = useState<Record<number, boolean>>({});
  const [filterLocation, setFilterLocation] = useState("");
  const [isFilterFormVisible, setIsFilterFormVisible] = useState(false);
  const [appliedFilterLocation, setAppliedFilterLocation] = useState("");
  const [locationSuggestions, setLocationSuggestions] = useState<string[]>([]);
  const [locations, setLocations] = useState<{ ln_locality: string }[]>([]);

  const [showContactPopup, setShowContactPopup] = useState(false);
  const [noResultsTimer, setNoResultsTimer] = useState<NodeJS.Timeout | null>(
    null
  );

  //new added
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [appliedSelectedLocations, setAppliedSelectedLocations] = useState<
    string[]
  >([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("Type");
  const [filterZone, setFilterZone] = useState("Zone");
  const [filterClassification, setFilterClassification] =
    useState("Classification");
  const [filterOwnerType, setFilterOwnerType] = useState("Owner Type");
  const [filterSellerType, setFilterSellerType] = useState("Seller Type");
  const [filterFacing, setFilterFacing] = useState("Facing");
  const [filterGated, setFilterGated] = useState("Gated");
  const [filterCorner, setFilterCorner] = useState("Corner");
  const [filterBoundaryWall, setFilterBoundaryWall] = useState("Boundary Wall");
  const [filterAuthorityApprove, setFilterAuthorityApprove] =
    useState("Authority Approve");
  const [sortBy, setSortBy] = useState("Sort by");
  const [priceRange, setPriceRange] = useState({ min: 0, max: 300000000 });
  const [areaRange, setAreaRange] = useState({ min: 0, max: 5000 });
  const [currentPage, setCurrentPage] = useState(1);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  // State for applied filters
  const [appliedFilterType, setAppliedFilterType] = useState("Type");
  const [appliedFilterZone, setAppliedFilterZone] = useState("Zone");
  const [appliedFilterClassification, setAppliedFilterClassification] =
    useState("Classification");
  const [appliedFilterOwnerType, setAppliedFilterOwnerType] =
    useState("Owner Type");
  const [appliedFilterSellerType, setAppliedFilterSellerType] =
    useState("Seller Type");
  const [appliedFilterFacing, setAppliedFilterFacing] = useState("Facing");
  const [appliedFilterGated, setAppliedFilterGated] = useState("Gated");
  const [appliedFilterCorner, setAppliedFilterCorner] = useState("Corner");
  const [appliedFilterBoundaryWall, setAppliedFilterBoundaryWall] =
    useState("Boundary Wall");
  const [appliedFilterAuthorityApprove, setAppliedFilterAuthorityApprove] =
    useState("Authority Approve");
  const [appliedSortBy, setAppliedSortBy] = useState("Sort by");
  const [appliedPriceRange, setAppliedPriceRange] = useState({
    min: 0,
    max: 300000000,
  });
  const [appliedAreaRange, setAppliedAreaRange] = useState({
    min: 0,
    max: 5000,
  });

  // const [slidersInitialized, setSlidersInitialized] = useState(false);
  const [dropdownOptions, setDropdownOptions] =
    useState<DropdownOptions | null>(null);
  // const sliderRangeRef = useRef<HTMLDivElement>(null);
  // const sliderRange2Ref = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const routerLocation = useLocation(); // Renamed to avoid conflict with global 'location'

  const handleWishlistClick = async (
    e: React.MouseEvent<HTMLAnchorElement>,
    landId: number
  ) => {
    e.preventDefault();

    const userId = Number(localStorage.getItem("frontendUserId")) || 0;

    if (!userId) {
      toast.info("Please login to save your activities.");
      setTimeout(() => {
        navigate("/authuser/login");
      }, 1000);
      return;
    }

    if (!lands) {
      toast.dismiss();
      toast.error("Invalid land data.");
      return;
    }

    try {
      const payload = {
        wl_landid: landId,
        wl_userid: userId,
      };

      const res = await axios.post(`${API_BASE}/api/wishlist/toggle`, payload);

      if (res.data.success) {
        setWishlistMap((prev) => ({
          ...prev,
          [landId]: res.data.isWishlisted,
        }));

        toast.dismiss();
        toast.success(res.data.message);
      } else {
        toast.dismiss();
        toast.error(res.data.message || "Something went wrong.");
      }
    } catch (err) {
      console.error("Wishlist error:", err);
      toast.dismiss();
      toast.error("Failed to update wishlist.");
    }
  };

  const checkWishlistStatus = async (landId: number) => {
    try {
      const userId = Number(localStorage.getItem("frontendUserId")) || 0;
      if (!userId) return;

      const res = await axios.get(`${API_BASE}/api/wishlist/list`, {
        params: { landid: landId, userid: userId },
      });

      if (res.data.success) {
        setWishlistMap((prev) => ({
          ...prev,
          [landId]: res.data.isWishlisted,
        }));
      }
    } catch (err) {
      console.error("Error checking wishlist:", err);
    }
  };

  useEffect(() => {
    const userId = Number(localStorage.getItem("frontendUserId")) || 0;
    if (!userId || lands.length === 0) return;

    lands.forEach((land) => {
      checkWishlistStatus(land.ln_id);
    });
  }, [lands]);

  const fetchDropdownOptions = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/alllandmaster/list`);

      if (res.data.success) {
        setDropdownOptions(res.data.AllLandMaster);
      } else {
        toast.error(res.data.message || "Failed to fetch dropdown options");
      }
    } catch (err) {
      handleApiError(err, navigate);
    }
  };

  const fetchLands = async (page = 1) => {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: showAll ? "0" : ITEMS_PER_PAGE.toString(),
        ...(showAll && { showAll: "true" }),
        ...(searchTerm && { location: searchTerm }),
        ...(appliedFilterLocation && { locality: appliedFilterLocation }),
        ...(appliedFilterType !== "Type" && {
          type: appliedFilterType.toLowerCase(),
        }),
        ...(appliedFilterZone !== "Zone" && {
          zone: appliedFilterZone.toLowerCase(),
        }),
        ...(appliedFilterClassification !== "Classification" && {
          classification: appliedFilterClassification.toLowerCase(),
        }),
        ...(appliedFilterOwnerType !== "Owner Type" && {
          ownerType:
            appliedFilterOwnerType === "Owner"
              ? "1"
              : appliedFilterOwnerType === "Agent"
              ? "2"
              : "",
        }),
        ...(appliedFilterSellerType !== "Seller Type" && {
          sellerType: appliedFilterSellerType.toLowerCase(),
        }),
        ...(appliedFilterFacing !== "Facing" && {
          facing: appliedFilterFacing.toLowerCase(),
        }),
        ...(appliedFilterGated !== "Gated" && {
          gated: appliedFilterGated.toLowerCase(),
        }),
        ...(appliedFilterCorner !== "Corner" && {
          corner: appliedFilterCorner.toLowerCase(),
        }),
        ...(appliedFilterBoundaryWall !== "Boundary Wall" && {
          boundarywall: appliedFilterBoundaryWall.toLowerCase(),
        }),
        ...(appliedFilterAuthorityApprove !== "Authority Approve" && {
          authorityApprove: appliedFilterAuthorityApprove.toLowerCase(),
        }),
        ...(appliedSortBy !== "Sort by" && {
          sortBy: appliedSortBy.toLowerCase(),
        }),
        ...(appliedPriceRange.min > 0 && {
          minPrice: appliedPriceRange.min.toString(),
        }),
        ...(appliedPriceRange.max < 300000000 && {
          maxPrice: appliedPriceRange.max.toString(),
        }),
        ...(appliedAreaRange.min > 0 && {
          minArea: appliedAreaRange.min.toString(),
        }),
        ...(appliedAreaRange.max < 5000 && {
          maxArea: appliedAreaRange.max.toString(),
        }),
      });

      const res = await axios.get(
        `${API_BASE}/api/landadd/frontend-list?${queryParams}`
      );

      if (res.data.success) {
        setLands(res.data.landadds || []);
        setPagination(res.data.pagination);
      } else {
        toast.error(res.data.message || "Failed to fetch lands");
      }
    } catch (err) {
      handleApiError(err, navigate);
    }
  };

  const handleShowAllToggle = (e: React.MouseEvent) => {
    e.preventDefault();

    if (showAll) {
      // When switching from "Show All" to paginated view
      setShowAll(false);
      setCurrentPage(1);
    } else {
      // When switching to "Show All"
      setShowAll(true);
      // Keep current page as is for a smoother transition
    }
  };

  // const handleShowPaginated = (e: React.MouseEvent) => {
  //   e.preventDefault();
  //   setShowAll(false);
  //   setCurrentPage(1);
  // };

  // const initializeSliders = () => {
  //   if (
  //     slidersInitialized ||
  //     !sliderRangeRef.current ||
  //     !sliderRange2Ref.current
  //   )
  //     return;

  //   try {
  //     $(sliderRangeRef.current).slider({
  //       range: true,
  //       min: 0,
  //       max: 300000000,
  //       values: [priceRange.min, priceRange.max],
  //       slide: function (event: any, ui: any) {
  //         setPriceRange({ min: ui.values[0], max: ui.values[1] });
  //         $("#slider-range-value1").text(formatShortPrice(ui.values[0]));
  //         $("#slider-range-value2").text(formatShortPrice(ui.values[1]));
  //       },
  //       change: function () {
  //         setCurrentPage(1);
  //       },
  //     });

  //     $(sliderRange2Ref.current).slider({
  //       range: true,
  //       min: 0,
  //       max: 5000,
  //       values: [areaRange.min, areaRange.max],
  //       slide: function (event: any, ui: any) {
  //         setAreaRange({ min: ui.values[0], max: ui.values[1] });
  //         $("#slider-range-value01").text(ui.values[0]);
  //         $("#slider-range-value02").text(ui.values[1]);
  //       },
  //       change: function () {
  //         setCurrentPage(1);
  //       },
  //     });

  //     $("#slider-range-value1").text(formatShortPrice(priceRange.min));
  //     $("#slider-range-value2").text(formatShortPrice(priceRange.max));
  //     $("#slider-range-value01").text(areaRange.min.toString());
  //     $("#slider-range-value02").text(areaRange.max.toString());

  //     setSlidersInitialized(true);
  //   } catch (error) {
  //     console.error("Error initializing sliders:", error);
  //   }
  // };

  const fetchLocationSuggestions = async (query: string) => {
    if (!query) {
      setLocationSuggestions([]);
      setLocations([]);
      return;
    }

    try {
      const res = await axios.get(
        `${API_BASE}/api/landdata/list?query=${query}`
      );

      if (res.data.success) {
        const rawSuggestions: string[] = res.data.landdata_location.map(
          (item: any) => item.ln_locality
        );

        // Remove duplicates
        const uniqueSuggestions = Array.from(new Set(rawSuggestions));

        // Prioritize: startsWith first, then contains
        const prioritized = uniqueSuggestions.sort((a, b) => {
          const queryLower = query.toLowerCase();
          const aStarts = a.toLowerCase().startsWith(queryLower);
          const bStarts = b.toLowerCase().startsWith(queryLower);

          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;
          return a.localeCompare(b); // fallback alphabetical
        });

        setLocationSuggestions(prioritized);
      } else {
        setLocationSuggestions([]);
        setLocations([]);
      }
    } catch (err) {
      console.error("Error fetching location suggestions:", err);
      setLocationSuggestions([]);
      setLocations([]);
    }
  };

  const resetFilters = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const queryParams = new URLSearchParams(routerLocation.search);
    const locality = queryParams.get("locality");
    // Reset all filter states
    setSearchTerm("");
    setFilterLocation("");
    setAppliedFilterLocation("");
    setLocationSuggestions([]);
    setFilterType("Type");
    setFilterZone("Zone");
    setFilterClassification("Classification");
    setFilterOwnerType("Owner Type");
    setFilterSellerType("Seller Type");
    setFilterFacing("Facing");
    setFilterGated("Gated");
    setFilterCorner("Corner");
    setFilterBoundaryWall("Boundary Wall");
    setFilterAuthorityApprove("Authority Approve");
    setSortBy("Sort by");
    setPriceRange({ min: 0, max: 300000000 });
    setAreaRange({ min: 0, max: 5000 });
    setAppliedSearchTerm("");
    setAppliedFilterType("Type");
    setAppliedFilterZone("Zone");
    setAppliedFilterClassification("Classification");
    setAppliedFilterOwnerType("Owner Type");
    setAppliedFilterSellerType("Seller Type");
    setAppliedFilterFacing("Facing");
    setAppliedFilterGated("Gated");
    setAppliedFilterCorner("Corner");
    setAppliedFilterBoundaryWall("Boundary Wall");
    setAppliedFilterAuthorityApprove("Authority Approve");
    setAppliedSortBy("Sort by");
    setAppliedPriceRange({ min: 0, max: 300000000 });
    setAppliedAreaRange({ min: 0, max: 5000 });
    setCurrentPage(1);

    // Clear URL query parameters
    // navigate("/lands", { replace: true });
    navigate(
      locality ? `/lands?locality=${encodeURIComponent(locality)}` : "/lands",
      { replace: true }
    );

    // Fetch initial land list
    fetchLands(1);
  };

  useEffect(() => {
    fetchDropdownOptions();
  }, []);

  // Read query parameters on page load
  // Parse query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(routerLocation.search);
    const type = queryParams.get("type");
    const minPrice = queryParams.get("minPrice");
    const maxPrice = queryParams.get("maxPrice");
    const minArea = queryParams.get("minArea");
    const maxArea = queryParams.get("maxArea");
    const ownerType = queryParams.get("ownerType");
    const gated = queryParams.get("gated");
    const facing = queryParams.get("facing");
    const corner = queryParams.get("corner");
    const boundarywall = queryParams.get("boundarywall");
    const authorityApprove = queryParams.get("authorityApprove");
    const locality = queryParams.get("locality");

    // new added
    const zone = queryParams.get("zone");
    const classification = queryParams.get("classification");
    const sellerType = queryParams.get("sellerType");
    const sortBy = queryParams.get("sortBy");

    // Reset all applied filters to default to ensure clean state
    setAppliedFilterType("Type");
    setAppliedFilterZone("Zone");
    setAppliedFilterClassification("Classification");
    setAppliedFilterOwnerType("Owner Type");
    setAppliedFilterSellerType("Seller Type");
    setAppliedFilterFacing("Facing");
    setAppliedFilterGated("Gated");
    setAppliedFilterCorner("Corner");
    setAppliedFilterBoundaryWall("Boundary Wall");
    setAppliedFilterAuthorityApprove("Authority Approve");
    setAppliedFilterLocation("");
    setAppliedPriceRange({ min: 0, max: 300000000 });
    setAppliedAreaRange({ min: 0, max: 5000 });
    setAppliedSortBy("Sort by");

    // Apply filters from URL parameters

    if (locality) {
      const localities = decodeURIComponent(locality).split(",");
      const capitalizedLocalities = localities
        .map((l) => l.charAt(0).toUpperCase() + l.slice(1).toLowerCase())
        .join(",");
      setFilterLocation(capitalizedLocalities);
      setAppliedFilterLocation(capitalizedLocalities);
    }

    if (type) {
      // Handle multiple plot types (comma-separated)
      const plotTypes = decodeURIComponent(type).split(",");
      const capitalizedType = plotTypes
        .map((t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase())
        .join(",");
      setFilterType(capitalizedType);
      setAppliedFilterType(capitalizedType);
    }

    if (zone) {
      const decodedZone = decodeURIComponent(zone);
      setFilterZone(decodedZone);
      setAppliedFilterZone(decodedZone);
    }

    if (classification) {
      const decodedClassification = decodeURIComponent(classification);
      setFilterClassification(decodedClassification);
      setAppliedFilterClassification(decodedClassification);
    }

    if (sellerType) {
      const decodedSellerType = decodeURIComponent(sellerType);
      const capitalizedSellerType =
        decodedSellerType.charAt(0).toUpperCase() +
        decodedSellerType.slice(1).toLowerCase();
      setFilterSellerType(capitalizedSellerType);
      setAppliedFilterSellerType(capitalizedSellerType);
    }

    if (facing) {
      const decodedFacing = decodeURIComponent(facing);
      const capitalizedFacing =
        decodedFacing.charAt(0).toUpperCase() +
        decodedFacing.slice(1).toLowerCase();
      setFilterFacing(capitalizedFacing);
      setAppliedFilterFacing(capitalizedFacing);
    }

    if (gated) {
      const decodedGated = decodeURIComponent(gated);
      const capitalizedGated =
        decodedGated.charAt(0).toUpperCase() +
        decodedGated.slice(1).toLowerCase();
      setFilterGated(capitalizedGated);
      setAppliedFilterGated(capitalizedGated);
    }

    if (corner) {
      const decodedCorner = decodeURIComponent(corner);
      const capitalizedCorner =
        decodedCorner.charAt(0).toUpperCase() +
        decodedCorner.slice(1).toLowerCase();
      setFilterCorner(capitalizedCorner);
      setAppliedFilterCorner(capitalizedCorner);
    }

    if (boundarywall) {
      const decodedBoundarywall = decodeURIComponent(boundarywall);
      const capitalizedBoundarywall =
        decodedBoundarywall.charAt(0).toUpperCase() +
        decodedBoundarywall.slice(1).toLowerCase();
      setFilterBoundaryWall(capitalizedBoundarywall);
      setAppliedFilterBoundaryWall(capitalizedBoundarywall);
    }

    if (authorityApprove) {
      const decodedAuthorityApprove = decodeURIComponent(authorityApprove);
      const capitalizedAuthorityApprove =
        decodedAuthorityApprove.charAt(0).toUpperCase() +
        decodedAuthorityApprove.slice(1).toLowerCase();
      setFilterAuthorityApprove(capitalizedAuthorityApprove);
      setAppliedFilterAuthorityApprove(capitalizedAuthorityApprove);
    }

    if (sortBy) {
      const decodedSortBy = decodeURIComponent(sortBy);
      const capitalizedSortBy =
        decodedSortBy.charAt(0).toUpperCase() +
        decodedSortBy.slice(1).toLowerCase();
      setSortBy(capitalizedSortBy);
      setAppliedSortBy(capitalizedSortBy);
    }

    // Price range
    if (minPrice && parseInt(minPrice) !== priceRange.min) {
      const min = parseInt(minPrice);
      setPriceRange((prev) => ({ ...prev, min }));
      setAppliedPriceRange((prev) => ({ ...prev, min }));
    }
    if (maxPrice && parseInt(maxPrice) !== priceRange.max) {
      const max = parseInt(maxPrice);
      setPriceRange((prev) => ({ ...prev, max }));
      setAppliedPriceRange((prev) => ({ ...prev, max }));
    }

    // Area range
    if (minArea && parseInt(minArea) !== areaRange.min) {
      const min = parseInt(minArea);
      setAreaRange((prev) => ({ ...prev, min }));
      setAppliedAreaRange((prev) => ({ ...prev, min }));
    }
    if (maxArea && parseInt(maxArea) !== areaRange.max) {
      const max = parseInt(maxArea);
      setAreaRange((prev) => ({ ...prev, max }));
      setAppliedAreaRange((prev) => ({ ...prev, max }));
    }

    // Owner type
    if (ownerType) {
      const ownerTypeValue =
        ownerType === "1" ? "Owner" : ownerType === "2" ? "Agent" : "";
      if (ownerTypeValue && filterOwnerType !== ownerTypeValue) {
        setFilterOwnerType(ownerTypeValue);
        setAppliedFilterOwnerType(ownerTypeValue);
      }
    }

    // Fetch lands with current page reset to 1
    setCurrentPage(1);
  }, [routerLocation.search]); // Only re-run when query string changes

  // Fetch lands based on applied filters
  useEffect(() => {
    fetchLands(currentPage);
  }, [
    currentPage,
    searchTerm,
    appliedFilterType,
    appliedFilterZone,
    appliedFilterClassification,
    appliedFilterOwnerType,
    appliedFilterSellerType,
    appliedFilterFacing,
    appliedFilterGated,
    appliedFilterCorner,
    appliedFilterBoundaryWall,
    appliedFilterAuthorityApprove,
    appliedSortBy,
    appliedPriceRange,
    appliedAreaRange,
    appliedFilterLocation,
    showAll,
  ]);

  useEffect(() => {
    setAppliedSortBy(sortBy);
    setCurrentPage(1);
  }, [sortBy]);

  useEffect(() => {
    // Clear existing timer
    if (noResultsTimer) {
      clearTimeout(noResultsTimer);
    }

    // If no results found, set timer to show popup after 2 seconds
    if (lands.length === 0 && pagination && pagination.total === 0) {
      const timer = setTimeout(() => {
        setShowContactPopup(true);
      }, 1000);
      setNoResultsTimer(timer);
    } else {
      setShowContactPopup(false);
    }

    // Cleanup on unmount
    return () => {
      if (noResultsTimer) {
        clearTimeout(noResultsTimer);
      }
    };
  }, [lands, pagination]);

  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     initializeSliders();
  //   }, 100);

  //   return () => clearTimeout(timer);
  // }, [slidersInitialized]);

  // useEffect(() => {
  //   if (slidersInitialized) {
  //     if (sliderRangeRef.current) {
  //       $(sliderRangeRef.current).slider("values", [
  //         priceRange.min,
  //         priceRange.max,
  //       ]);
  //     }
  //     if (sliderRange2Ref.current) {
  //       $(sliderRange2Ref.current).slider("values", [
  //         areaRange.min,
  //         areaRange.max,
  //       ]);
  //     }
  //   }
  // }, [priceRange, areaRange, slidersInitialized]);

  const filteredLands = lands.filter(
    (land) => land.ln_status === 1 && land.ln_admin_approve === 1
  );

  const toggleFilterForm = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsFilterFormVisible(!isFilterFormVisible);
  };

  const handleLocationChange = (locality: string) => {
    setSelectedLocations((prev) => {
      if (prev.includes(locality)) {
        return prev.filter((loc) => loc !== locality);
      } else {
        return [...prev, locality];
      }
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // setCurrentPage(1);
    setSearchTerm(value);

    if (value) {
      const filtered = locationSuggestions
        .map((suggestion) => ({ ln_locality: suggestion }))
        .filter((loc) =>
          loc.ln_locality.toLowerCase().includes(value.toLowerCase())
        );
      setLocations(filtered.slice(0, 10)); // Limit to 10 results
    } else {
      // Show all available locations when search is empty
      const allLocations = locationSuggestions.map((suggestion) => ({
        ln_locality: suggestion,
      }));
      setLocations(allLocations.slice(0, 10));
    }
  };

  const handlePageChange = (newPage: number) => {
    if (pagination && newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Function to generate pagination items
  const renderPaginationItems = () => {
    if (!pagination) return null;

    const totalPages = pagination.totalPages;
    const current = currentPage;
    const items = [];

    // Always show first page
    items.push(
      <li key={1} className={`page-item ${current === 1 ? "active" : ""}`}>
        <a
          className="page-link"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handlePageChange(1);
          }}
        >
          1
        </a>
      </li>
    );

    // Calculate start and end pages
    let startPage = Math.max(2, current - 2);
    let endPage = Math.min(totalPages - 1, current + 2);

    // Add ellipsis if needed after first page
    if (startPage > 2) {
      items.push(
        <li key="ellipsis1" className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    }

    // Add page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <li key={i} className={`page-item ${current === i ? "active" : ""}`}>
          <a
            className="page-link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(i);
            }}
          >
            {i}
          </a>
        </li>
      );
    }

    // Add ellipsis if needed before last page
    if (endPage < totalPages - 1) {
      items.push(
        <li key="ellipsis2" className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    }

    // Always show last page if there is more than one page
    if (totalPages > 1) {
      items.push(
        <li
          key={totalPages}
          className={`page-item ${current === totalPages ? "active" : ""}`}
        >
          <a
            className="page-link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(totalPages);
            }}
          >
            {totalPages}
          </a>
        </li>
      );
    }

    return items;
  };

  return (
    <FrontendLayout>
      <ToastContainer />

      {/* Added the pagination styles */}
      <style>
        {`
          .pagination.circle .page-link {
            border-radius: 50% !important;
            width: 45px; height: 45px;
            line-height: 30px;
            text-align: center;
            margin: 0 4px;
            background: #f8f9fa;
            color: #824b76;
            transition: all 0.3s ease;
          }
          .pagination.circle .page-link:hover {
            background: #824b76;
            color: #fff;
          }
          .pagination.circle .page-item.active .page-link {
            background: #824b76;
            border-color: #824b76;
            color: #fff;
            font-weight: bold;
          }
            .rc-slider {
            margin: 10px 0;
          }
          .rc-slider-rail {
            background-color: #ddd;
            height: 8px;
          }
          .rc-slider-track {
            background-color: #824b76;
            height: 8px;
          }
          .rc-slider-handle {
            border: 2px solid #824b76;
            background-color: #fff;
            width: 20px;
            height: 20px;
            margin-top: -6px;
            cursor: pointer;
          }
          .rc-slider-handle:hover {
            border-color: #5a2e4f;
          }
          .rc-slider-mark-text {
            color: #333;
          }
           .get-help-button {
            position: fixed;
            bottom: 526px;
            right: -17px;
            background-color: #824b76;
            color: #fff;
            padding: 12px 32px 12px 20px;
            border-radius: 50px 0 0 50px;
            font-weight: 600;
            z-index: 1000;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
            animation: blink 1.5s infinite;
            transition: background-color 0.3s ease;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .get-help-button:hover {
            background-color: #5a2e4f;
            text-decoration: none;
          }
          .shiver-icon {
            animation: shiver 0.5s 2;
            animation-delay: 2s;
            animation-fill-mode: forwards;
          }
          @keyframes blink {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
          @keyframes shiver {
            0% { transform: translate(0, 0) rotate(0deg); }
            25% { transform: translate(0.5px, 0.5px) rotate(0.5deg); }
            50% { transform: translate(-0.5px, -0.5px) rotate(-0.5deg); }
            75% { transform: translate(0.5px, -0.5px) rotate(0.5deg); }
            100% { transform: translate(-0.5px, 0.5px) rotate(-0.5deg); }
          }
        `}
      </style>

      <section className="flat-title style-2 mb-4">
        <div className="tf-container">
          <div className="row">
            <div className="col-lg-12">
              <div className="title-inner mb-0">
                <ul className="breadcrumb">
                  <li>
                    <Link to="/" className="home fw-6 text-color-3">
                      Home
                    </Link>
                  </li>
                  <li>Lands</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="flat-title style-2 mb-4 sticky-top bg-white backToTop">
        <div className="tf-container rounded-bottom" id="mainSticky">
          <div className="row">
            <div className="col-lg-12 pt-3">
              <div className="wg-filter style-2 relative z-31 form">
                <ul className="float-list">
                  <li>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by location, plot type"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </li>
                  <li>
                    <select
                      className="form-select nice-select"
                      value={filterType}
                      onChange={(e) => {
                        setFilterType(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option>Type</option>
                      {dropdownOptions?.plotTypes
                        .filter((type) => type.pt_status === 1)
                        .map((type) => (
                          <option key={type.pt_id} value={type.pt_name}>
                            {type.pt_name}
                          </option>
                        ))}
                    </select>
                  </li>
                  <li>
                    <select
                      className="form-select nice-select"
                      value={filterZone}
                      onChange={(e) => {
                        setFilterZone(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option>Zone</option>
                      {dropdownOptions?.zones
                        .filter((zone) => zone["Z-status"] === 1)
                        .map((zone) => (
                          <option key={zone.z_id} value={zone.z_name}>
                            {zone.z_name}
                          </option>
                        ))}
                    </select>
                  </li>
                  <li>
                    <select
                      className="form-select nice-select"
                      value={filterClassification}
                      onChange={(e) => {
                        setFilterClassification(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option>Classification</option>
                      {dropdownOptions?.classifications
                        .filter((cls) => cls.cls_status === 1)
                        .map((cls) => (
                          <option key={cls.cls_id} value={cls.cls_name}>
                            {cls.cls_name}
                          </option>
                        ))}
                    </select>
                  </li>
                  <li>
                    <div className="form-title style-2">
                      <div className="wrap-btn">
                        <div
                          className={`btn-filter tf-btn bg-color-primary pd-3 fw-6 ${
                            isFilterFormVisible ? "show-form" : ""
                          }`}
                          onClick={toggleFilterForm}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="icons">
                            Explore Advance Filters
                            {isFilterFormVisible ? (
                              <i className="bi bi-x-lg ms-2 fw-6"></i> // Cross icon when filter is open
                            ) : (
                              <i className="bi bi-sliders2 ms-2"></i> // Sliders icon when filter is closed
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="form-title style-2">
                      <div className="wrap-btn">
                        <a
                          href="#"
                          className="tf-btn bg-color-primary pd-3 fw-6"
                          onClick={(e) => {
                            e.preventDefault();
                            setAppliedSearchTerm(searchTerm);
                            setAppliedFilterType(filterType);
                            setAppliedFilterZone(filterZone);
                            setAppliedFilterClassification(
                              filterClassification
                            );
                            setAppliedFilterOwnerType(filterOwnerType);
                            setAppliedFilterSellerType(filterSellerType);
                            setAppliedFilterFacing(filterFacing);
                            setAppliedFilterGated(filterGated);
                            setAppliedFilterCorner(filterCorner);
                            setAppliedFilterBoundaryWall(filterBoundaryWall);
                            setAppliedFilterAuthorityApprove(
                              filterAuthorityApprove
                            );
                            setAppliedPriceRange(priceRange);
                            setAppliedAreaRange(areaRange);
                            setAppliedFilterLocation(filterLocation);
                            setCurrentPage(1);
                            setIsFilterFormVisible(false);
                          }}
                        >
                          Search <i className="icon-MagnifyingGlass fw-6"></i>
                        </a>
                        <a
                          href="/lands"
                          className="tf-btn bg-secondary pd-3 fw-6"
                          onClick={resetFilters}
                        >
                          Reset
                        </a>
                      </div>
                    </div>
                  </li>
                </ul>

                <div
                  className={`wd-search-form ${
                    isFilterFormVisible ? "show" : ""
                  }`}
                >
                  <div className="group-price">
                    <div className="widget-price">
                      <div className="box-title-price">
                        <span className="title-price">Price</span>
                        <div className="caption-price">
                          <span>from</span>
                          &nbsp;
                          <span className="value fw-6">
                            {formatShortPrice(priceRange.min)}
                          </span>
                          &nbsp;
                          <span>to</span>
                          &nbsp;
                          <span className="value fw-6">
                            {formatShortPrice(priceRange.max)}
                          </span>
                        </div>
                      </div>
                      <Slider
                        range
                        min={0}
                        max={300000000}
                        value={[priceRange.min, priceRange.max]}
                        onChange={(value) => {
                          if (Array.isArray(value)) {
                            setPriceRange({ min: value[0], max: value[1] });
                          }
                        }}
                        onAfterChange={() => setCurrentPage(1)}
                      />
                    </div>
                    <div className="widget-price">
                      <div className="box-title-price">
                        <span className="title-price">Plot Area (Sq ft.)</span>
                        <div className="caption-price">
                          <span>from</span>
                          &nbsp;
                          <span className="value fw-6">{areaRange.min}</span>
                          &nbsp;
                          <span>to</span>
                          &nbsp;
                          <span className="value fw-6">{areaRange.max}</span>
                        </div>
                      </div>
                      <Slider
                        range
                        min={0}
                        max={5000}
                        value={[areaRange.min, areaRange.max]}
                        onChange={(value) => {
                          if (Array.isArray(value)) {
                            setAreaRange({ min: value[0], max: value[1] });
                          }
                        }}
                        onAfterChange={() => setCurrentPage(1)}
                      />
                    </div>
                  </div>
                  <div className="row form">
                    <div className="col-lg-3 pb-3">
                      <select
                        className="form-select nice-select"
                        value={filterSellerType}
                        onChange={(e) => {
                          setFilterSellerType(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option>Seller Type</option>
                        <option>Individual</option>
                        <option>Multiple</option>
                      </select>
                    </div>
                    <div className="col-lg-3 pb-3">
                      <select
                        className="form-select nice-select"
                        value={filterFacing}
                        onChange={(e) => {
                          setFilterFacing(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option>Facing</option>
                        {dropdownOptions?.facing
                          .filter((face) => face.fc_status === 1)
                          .map((face) => (
                            <option key={face.fc_id} value={face.fc_name}>
                              {face.fc_name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="col-lg-3 pb-3">
                      <select
                        className="form-select nice-select"
                        value={filterOwnerType}
                        onChange={(e) => {
                          setFilterOwnerType(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option>Posted By</option>
                        <option>Owner</option>
                        <option>Agent</option>
                      </select>
                    </div>
                    {/* <div className="col-lg-3 pb-3 position-relative">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Filter by Location"
                        value={filterLocation}
                        onChange={(e) => {
                          setFilterLocation(e.target.value);
                          fetchLocationSuggestions(e.target.value);
                        }}
                      />
                      {locationSuggestions.length > 0 && (
                        <ul
                          className="list-group position-absolute w-100 zindex-dropdown shadow-sm"
                          style={{
                            top: "100%",
                            left: 0,
                            maxHeight: "200px",
                            overflowY: "auto",
                            backgroundColor: "#fff",
                            border: "1px solid #ddd",
                            borderTop: "none",
                          }}
                        >
                          {locationSuggestions.map((suggestion, index) => (
                            <li
                              key={index}
                              className="list-group-item list-group-item-action"
                              style={{
                                cursor: "pointer",
                                padding: "10px 15px",
                                borderBottom:
                                  index === locationSuggestions.length - 1
                                    ? "none"
                                    : "1px solid #eee",
                              }}
                              onClick={() => {
                                setFilterLocation(suggestion);
                                setLocationSuggestions([]);
                              }}
                              onMouseEnter={(e) => {
                                (
                                  e.target as HTMLElement
                                ).style.backgroundColor = "#f7f7f7";
                              }}
                              onMouseLeave={(e) => {
                                (
                                  e.target as HTMLElement
                                ).style.backgroundColor = "#fff";
                              }}
                            >
                              <i className="bi bi-geo-alt me-2 text-primary"></i>
                              {suggestion}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div> */}
                    <div className="col-lg-3 pb-3">
                      <select
                        className="form-select nice-select"
                        value={filterGated}
                        onChange={(e) => {
                          setFilterGated(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option>Gated</option>
                        <option>Yes</option>
                        <option>No</option>
                      </select>
                    </div>
                    <div className="col-lg-3 pb-3">
                      <select
                        className="form-select nice-select"
                        value={filterCorner}
                        onChange={(e) => {
                          setFilterCorner(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option>Corner</option>
                        <option>Yes</option>
                        <option>No</option>
                      </select>
                    </div>
                    <div className="col-lg-3 pb-3">
                      <select
                        className="form-select nice-select"
                        value={filterAuthorityApprove}
                        onChange={(e) => {
                          setFilterAuthorityApprove(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option>Authority Approve</option>
                        <option>Yes</option>
                        <option>No</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-neighborhoods style-2 mb-5">
        <div className="tf-container tf-spacing-1 pt-0">
          <div className="row d-flex justify-content-between">
            <div className="col-12 col-sm-12">
              <hr className="mt-3" />
            </div>
            <div className="col-6 col-sm-6">
              <div className="heading flex justify-between pb-3 mb-4 align-items-center">
                <div className="title text-8 fw-6 text-color-heading">
                  Land Listing
                </div>
              </div>
              {pagination && pagination.total > 0 && (
                <div className="results-count text-muted mb-2">
                  {pagination.total} Land{pagination.total !== 1 ? "s" : ""}{" "}
                  found for your search.
                </div>
              )}
            </div>

            <div className="col-6 col-sm-3 pb-5">
              <select
                className="form-select nice-select"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option>Sort by</option>
                <option>Newest</option>
                <option>Oldest</option>
              </select>
            </div>
          </div>
          <div className="row mt-5">
            {lands.length > 0 ? (
              lands.map((land, index) => (
                <div className="col-12 col-sm-4 pb-5" key={land.ln_id}>
                  <div className="box-location mb-5 hover-img">
                    <div className="plot-location">
                      <i className="bi bi-geo-alt me-1"></i>
                      <div className="plot-location-text ms-2">
                        {land.ln_locality},<br /> {land.ln_address}
                      </div>
                    </div>
                    <div className={`plot-details3 sec${(index % 5) + 1}`}>
                      <div className="plot-details-row">
                        {land.ln_total_sqft_area} sqft
                        <br />
                        Plot Area
                      </div>
                      <div className="plot-details-row">{land.ln_plottype}</div>
                      <div className="plot-details-row no-border">
                        {new Date(land.ln_post_date).toLocaleDateString(
                          "en-GB",
                          {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          }
                        )}
                        <br />
                        Posted on
                      </div>
                    </div>
                    <div className="image-wrap">
                      <Link to={`/${land.ln_seo_url}`}>
                        <img
                          className="lazyload"
                          src={
                            land.image_files && land.image_files.length > 0
                              ? `${API_BASE}/Uploads/lands/${land.image_files[0]}`
                              : `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/No-Image-Placeholder.jpg`
                          }
                          alt="Plot"
                        />
                      </Link>
                    </div>
                    <div className="content">
                      <h4 className="text_white">
                        <i className="bi bi-currency-rupee me-1"></i>
                        {formatShortPrice(land.ln_price)} <br />
                        <small>
                          <i className="bi bi-currency-rupee me-1"></i>
                          {formatNumberIndian(land.ln_total_area_sqft)} per sqft
                        </small>
                      </h4>
                    </div>
                    <a
                      href="#"
                      className="save"
                      onClick={(e) => handleWishlistClick(e, land.ln_id)}
                    >
                      <i
                        className={`bi ${
                          wishlistMap[land.ln_id]
                            ? "bi-heart-fill text-danger"
                            : "bi-heart"
                        }`}
                      />
                    </a>
                    <Link to={`/${land.ln_seo_url}`} className="click">
                      <i className="bi bi-arrow-up-right-circle-fill"></i>
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-lg-12">
                <NoResults description="Try adjusting your search or filter to find what you're looking for." />
              </div>
            )}
          </div>

          {/* New Pagination Design */}

          <div className="d-flex justify-content-between mt-4">
            {pagination && pagination.total > ITEMS_PER_PAGE && (
              <a
                href="#"
                className="tf-btn bg-secondary pd-3 fw-6 ms-2"
                onClick={handleShowAllToggle}
              >
                {showAll ? "Show Less" : "Show All"}
              </a>
            )}
            {pagination && pagination.totalPages > 1 && !showAll && (
              <ul className="pagination justify-content-center circle">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <a
                    className="page-link"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage > 1) handlePageChange(currentPage - 1);
                    }}
                  >
                    «
                  </a>
                </li>

                {renderPaginationItems()}

                <li
                  className={`page-item ${
                    currentPage === pagination.totalPages ? "disabled" : ""
                  }`}
                >
                  <a
                    className="page-link"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage < pagination.totalPages)
                        handlePageChange(currentPage + 1);
                    }}
                  >
                    »
                  </a>
                </li>
              </ul>
            )}
          </div>
        </div>
      </section>
      {/* Get Help Button */}
      <a
        href="#"
        className="get-help-button"
        onClick={(e) => {
          e.preventDefault();
          setShowContactPopup(true);
        }}
      >
        <i className="bi bi-question-circle shiver-icon"></i>
        Get Help
      </a>
      <ContactPopup
        show={showContactPopup}
        onClose={() => setShowContactPopup(false)}
      />
    </FrontendLayout>
  );
};

export default LandList;
